/**
 * Layout split-screen de l'éditeur avancé (formulaire à gauche, aperçu
 * mobile à droite) et états de chargement / erreur (extrait de
 * ExerciceAdvancedEditor, déplacement pur).
 */
import React from 'react';
import { Box, Container, Typography, Paper, Grid, Button, CircularProgress, Alert } from '@mui/material';
import { ArrowBack } from '@mui/icons-material';
import { Exercice } from '../../../types';
import MobilePreview from '../../common/MobilePreview';
import ExerciceMobileView from '../ExerciceMobileView';
import ExerciceFormWrapper from './ExerciceFormWrapper';
import { ExerciceAdvancedEditorState } from './useExerciceAdvancedEditor';

/** Aperçu mobile en temps réel (colonne droite). */
const PreviewColumn: React.FC<{ previewData: Omit<Exercice, 'id'> | null; chapitreTitre: string; leconTitre: string }> = ({
  previewData,
  chapitreTitre,
  leconTitre,
}) => (
  <Grid item xs={12} lg={5}>
    <Box
      sx={{
        position: 'sticky',
        top: 16,
        maxHeight: 'calc(100vh - 32px)',
        overflowY: 'auto',
      }}
    >
      <MobilePreview title="Aperçu Mobile en Temps Réel">
        {previewData ? (
          <ExerciceMobileView
            exercice={previewData}
            chapitreTitre={chapitreTitre}
            leconTitre={leconTitre}
          />
        ) : (
          <Box
            display="flex"
            alignItems="center"
            justifyContent="center"
            minHeight="400px"
          >
            <Typography color="textSecondary">Aucune donnée à prévisualiser</Typography>
          </Box>
        )}
      </MobilePreview>
    </Box>
  </Grid>
);

/** Formulaire d'édition à gauche, aperçu mobile à droite (masquable). */
export const EditorSplitLayout: React.FC<{ editor: ExerciceAdvancedEditorState; exercice: Exercice }> = ({
  editor,
  exercice,
}) => (
  <Grid container spacing={3}>
    {/* Formulaire d'édition - Gauche */}
    <Grid item xs={12} lg={editor.showPreview ? 7 : 12}>
      <Paper sx={{ p: 3, height: '100%' }}>
        <ExerciceFormWrapper
          exercice={exercice}
          returnPath={editor.returnPath}
          saving={editor.saving}
          readOnly={editor.isReadOnly}
          onSubmit={editor.handleSubmit}
          onCancel={editor.handleCancel}
          onChange={editor.handleFormChange}
          onFieldBlur={editor.handleFieldBlur}
        />
      </Paper>
    </Grid>

    {/* Preview Mobile - Droite */}
    {editor.showPreview && (
      <PreviewColumn
        previewData={editor.previewData}
        chapitreTitre={editor.chapitreTitre}
        leconTitre={editor.leconTitre}
      />
    )}
  </Grid>
);

/** Chargement de l'exercice. */
export const EditorLoading: React.FC = () => (
  <Box
    display="flex"
    flexDirection="column"
    justifyContent="center"
    alignItems="center"
    minHeight="100vh"
    sx={{ bgcolor: '#f5f5f5' }}
  >
    <CircularProgress size={48} />
    <Typography variant="h6" sx={{ mt: 2 }} color="textSecondary">
      Chargement de l'exercice...
    </Typography>
  </Box>
);

/** Erreur de chargement ou exercice introuvable, avec retour à la liste. */
export const EditorError: React.FC<{ error: string | null; onCancel: () => void }> = ({ error, onCancel }) => (
  <Container maxWidth="lg" sx={{ py: 4 }}>
    <Alert severity="error">{error || 'Exercice introuvable'}</Alert>
    <Button startIcon={<ArrowBack />} onClick={onCancel} sx={{ mt: 2 }}>
      Retour à la liste
    </Button>
  </Container>
);
